/**
 * PATH: packages/ui/src/kanban/card-surface/projections/useCardSurfaceCommands.ts
 * FILE_ID: UI-KANBAN-CARDSURFACE-DENSECOMMANDS-001
 * PURPOSE: Route dense card projections through the shared runtime command seam.
 * WHY: A surface marker alone cannot supply availability gates or command telemetry.
 * FLOW: surface/card/input -> available runtime command -> legacy fallback when absent.
 * DEPENDENCIES: CardCapabilityProvider; Card command contract.
 * RELATED: CardTileSurface.tsx; BoardTableRowTr.tsx; GanttBar.tsx.
 */
import { useCallback, useMemo } from 'react'

import type { Card } from '../../types'
import type {
  CardCommandId,
  CardCommandInput,
  CardSurfaceId,
} from '../contract'
import { useOptionalCardSurfaceRuntime } from '../runtime/useCardCapabilityRuntime'

type OpenFallback = (card: Card) => void
type ContextFallback = (
  card: Card,
  anchor: { x: number; y: number },
) => void

export function useCardSurfaceCommands(surfaceId: CardSurfaceId) {
  const runtime = useOptionalCardSurfaceRuntime()
  const execute = useCallback(
    (
      commandId: CardCommandId,
      card: Card,
      input: CardCommandInput = {},
    ) => {
      const command = runtime?.commands[commandId]
      if (command?.availability !== 'available') return undefined
      return command.execute({ card, surfaceId, ...input })
    },
    [runtime, surfaceId],
  )

  const open = useCallback(
    (
      card: Card,
      fallback?: OpenFallback,
      input: CardCommandInput = {},
    ) => {
      const result = execute('open', card, input)
      if (result) {
        void result
        return
      }
      if (runtime?.mode !== 'read-only') fallback?.(card)
    },
    [execute, runtime?.mode],
  )

  const openContextMenu = useCallback(
    (
      card: Card,
      anchor: { x: number; y: number },
      fallback?: ContextFallback,
    ) => {
      const result = execute('open-context-menu', card, { anchor })
      if (result) {
        void result
        return
      }
      if (runtime?.mode !== 'read-only') fallback?.(card, anchor)
    },
    [execute, runtime?.mode],
  )

  return useMemo(
    () => ({
      execute,
      open,
      openContextMenu,
      runtimeMode: runtime?.mode,
    }),
    [execute, open, openContextMenu, runtime?.mode],
  )
}
